import React, { useMemo, useCallback, useState } from 'react';
import { Grid, Box } from '@mui/material';
import isValid from 'date-fns/isValid';
import format from 'date-fns/format';
import { FiltersAutocomplete } from '../FiltersAutocomplete/FiltersAutocomplete';
import { FilterDatePicker } from '../FilterDatePicker/FilterDatePicker';
import { dateRangeObject, defaultDateRangeList, DateRangeOption } from '../../consts/dateRange';
import { calculateDateRange, matchesDateRange } from '../../helpers/dateRange';
import { DATE_FORMAT } from '../../consts/constants';
import { toLocalDateString } from '../../helpers/date';
import { endOfDay, startOfDay } from 'date-fns';

interface DateRangeFilterProps {
  label: string;
  fromField: string;
  toField: string;
  fromValue: string | null;
  toValue: string | null;
  onChange: (updates: Record<string, string | null>) => void;
  options?: DateRangeOption[];
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  label,
  fromField,
  toField,
  fromValue,
  toValue,
  onChange,
  options = defaultDateRangeList,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const selectorValue = useMemo(() => {
    if (isEditing) {
      return dateRangeObject.customRange.label;
    }

    if (!fromValue && !toValue) {
      return dateRangeObject.anyTime.label;
    }

    const matchedOption = options.find(
      (option) =>
        option.value !== dateRangeObject.customRange.value && matchesDateRange(fromValue, toValue, option.value)
    );

    return matchedOption ? matchedOption.label : dateRangeObject.customRange.label;
  }, [fromValue, toValue, isEditing, options]);

  const handleSelectorChange = useCallback(
    (_: string, newValue: string) => {
      const option = options.find((opt) => opt.label === newValue);
      if (!option) return;

      if (option.value === dateRangeObject.customRange.value) {
        setIsEditing(true);
        return;
      }

      setIsEditing(false);

      if (option.value === dateRangeObject.anyTime.value) {
        onChange({ [fromField]: null, [toField]: null });
        return;
      }

      const range = calculateDateRange(option.value);
      if (range) {
        onChange({ [fromField]: range.from, [toField]: range.to });
      }
    },
    [fromField, toField, onChange, options]
  );

  const handleDateChange = useCallback(
    (field: string, type: 'from' | 'to') => (date: Date | string | null) => {
      if (!date) {
        onChange({ [field]: null });
        return;
      }
      const getToOrFromDateWithDefault = type === 'from' ? startOfDay : endOfDay;
      const dateObj =
        date instanceof Date ? getToOrFromDateWithDefault(date) : getToOrFromDateWithDefault(new Date(date));
      if (isValid(dateObj)) {
        onChange({ [field]: format(dateObj, DATE_FORMAT) });
      }
    },
    [onChange]
  );

  const optionLabels = useMemo(() => options.map((item) => item.label), [options]);

  return (
    <Box>
      <FiltersAutocomplete
        name={fromField}
        value={selectorValue}
        handleChange={handleSelectorChange}
        options={optionLabels}
        label={label}
      />
      {selectorValue === dateRangeObject.customRange.label && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <FilterDatePicker
                label="From"
                value={fromValue}
                onChange={handleDateChange(fromField, 'from')}
                maxDate={toLocalDateString(toValue) || undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FilterDatePicker
                label="To"
                value={toValue}
                onChange={handleDateChange(toField, 'to')}
                minDate={toLocalDateString(fromValue) || undefined}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};
